// Layar Master › Tarif Borongan — logika MURNI (T110 + T111, Owner 2 Nov 2026: "benahin dulu aja bro biar tidak
// membingungkan"). Aturan bisnis tarif TIDAK berubah di sini (berversi K62, tidak mundur K64, ambang Kepala Cabang →
// Owner K78, tarif dikunci per tanggal FA): berkas ini hanya menata APA yang ditampilkan dan KALIMAT ringkasannya.
import { formatIDR, formatDate } from "./format";

// T110: jenis pekerjaan buatan uji (berkode awalan "UJI-", K84 `UJI-BORONGAN` dkk.) DISEMBUNYIKAN dari layar dan
// pilihan dialog. Barisnya tetap permanen di database (K84 — trigger K62 menolak hapus); PRODUCTION tetap ditolak
// selama masih ada yang aktif (preflight TARIF_TANPA_BARIS_UJI, routers/app_mode.py).
export const AWALAN_UJI = "UJI-";

export function kodeJenis(row) {
  return String(row?.work_type_code ?? row?.work_type?.code ?? row?.code ?? "");
}

export function dataUji(row) {
  return kodeJenis(row).toUpperCase().startsWith(AWALAN_UJI);
}

// Urutan komponen di tiap kartu wilayah: dasar dulu, tambahan sesudahnya.
const URUT_KOMPONEN = { DASAR_JERSEY: 0, DASAR_CELANA: 1, LENGAN_PANJANG: 2, POLA_RAGLAN: 3, KERAH: 4 };

export const LABEL_KOMPONEN = {
  DASAR_JERSEY: "Dasar · per potong atasan",
  DASAR_CELANA: "Dasar · per potong celana",
  LENGAN_PANJANG: "Tambahan · per jersey lengan panjang",
  POLA_RAGLAN: "Tambahan · per jersey pola raglan",
  KERAH: "Tambahan · per jersey berkerah ini",
};

export function kunciKombinasi(row) {
  return `${row?.region_id}|${row?.work_type_id}`;
}

/** Satu baris per (wilayah × jenis pekerjaan): yang punya tarif berlaku (`items`) DAN yang belum (`kosong`),
 *  tanpa data uji, diurutkan wilayah → komponen → nama. Baris tanpa tarif ditandai `ada: false`. */
export function barisTarif(items = [], kosong = []) {
  const semua = [
    ...items.map((r) => ({ ...r, ada: true })),
    ...kosong.map((r) => ({
      ...r,
      id: `kosong-${kunciKombinasi(r)}`,
      region_name: r.region_name ?? r.region?.name ?? null,
      work_type_name: r.work_type_name ?? r.work_type?.name ?? null,
      rate_per_pcs_idr: null,
      ada: false,
    })),
  ].filter((r) => !dataUji(r));
  const urut = (r) => URUT_KOMPONEN[r.jenis_komponen] ?? 9;
  return semua.sort((a, b) => String(a.region_name || "").localeCompare(String(b.region_name || ""))
    || urut(a) - urut(b)
    || String(a.work_type_name || "").localeCompare(String(b.work_type_name || "")));
}

/** Kelompokkan baris (yang sudah diurutkan) per wilayah, urutan dipertahankan. */
export function kelompokWilayah(baris) {
  const m = new Map();
  for (const r of baris) {
    if (!m.has(r.region_id)) m.set(r.region_id, { region_id: r.region_id, region_name: r.region_name, baris: [] });
    m.get(r.region_id).baris.push(r);
  }
  return [...m.values()];
}

/** Contoh hitung per wilayah: 1 jersey set = dasar atasan + dasar celana (tanpa tambahan). */
export function contohJerseySet(barisWilayah) {
  const atasan = barisWilayah.find((r) => r.jenis_komponen === "DASAR_JERSEY" && r.ada);
  const celana = barisWilayah.find((r) => r.jenis_komponen === "DASAR_CELANA" && r.ada);
  if (!atasan || !celana) {
    const kurang = [!atasan && "atasan", !celana && "celana"].filter(Boolean).join(" dan ");
    return { lengkap: false, kalimat: `Contoh hitung belum bisa ditampilkan: tarif dasar ${kurang} belum ada.` };
  }
  const total = Number(atasan.rate_per_pcs_idr) + Number(celana.rate_per_pcs_idr);
  return {
    lengkap: true,
    total,
    kalimat: `1 jersey set (atasan + celana) = ${formatIDR(total)} upah jahit `
      + `(atasan ${formatIDR(atasan.rate_per_pcs_idr)} + celana ${formatIDR(celana.rate_per_pcs_idr)}), `
      + "belum termasuk tambahan kerah, lengan panjang, atau raglan.",
  };
}

/** Batas perubahan yang memunculkan peringatan "periksa lagi angkanya" (cegah salah ketik nol). */
export const AMBANG_PERINGATAN_PERSEN = 20;

/** Ringkasan sebelum simpan. `lama` = tarif yang berlaku sekarang (null = tarif pertama). */
export function ringkasanUbahTarif({ wilayah, jenis, lama, baru, sejak }) {
  const y = Number(baru);
  const x = lama === null || lama === undefined ? null : Number(lama);
  const awal = `${wilayah || "—"} · ${jenis || "—"}: `;
  const ekor = `berlaku untuk FA yang dibuat mulai ${formatDate(sejak)}; FA sebelumnya tetap memakai tarif lama.`;
  if (x === null || !Number.isFinite(x) || x <= 0) {
    return { arah: "PERTAMA", persen: null, besar: false,
      kalimat: `${awal}tarif pertama ${formatIDR(y)} per potong, ${ekor}` };
  }
  const persen = ((y - x) / x) * 100;
  const arah = y > x ? "NAIK" : y < x ? "TURUN" : "SAMA";
  const teksPersen = `${Math.abs(persen).toFixed(2).replace(/\.?0+$/, "").replace(".", ",")}%`;
  const teksArah = arah === "NAIK" ? `naik ${teksPersen}` : arah === "TURUN" ? `turun ${teksPersen}` : "tidak berubah";
  return {
    arah,
    persen,
    besar: Math.abs(persen) > AMBANG_PERINGATAN_PERSEN,
    kalimat: `${awal}${formatIDR(x)} → ${formatIDR(y)} (${teksArah}), ${ekor}`,
  };
}

/** Status tiap versi di riwayat SATU kombinasi, memakai penanda server `belum_berlaku` (tanggal cabang WIB/WITA),
 *  bukan jam peramban. Masukan diurutkan terbaru dulu. */
export function statusRiwayat(versi = []) {
  const urut = [...versi].sort((a, b) => (a.berlaku_sejak < b.berlaku_sejak ? 1
    : a.berlaku_sejak > b.berlaku_sejak ? -1
      : String(b.created_at || "").localeCompare(String(a.created_at || ""))));
  let aktifAda = false;
  return urut.map((v) => {
    let status;
    if (v.belum_berlaku) status = "TERJADWAL";
    else if (!aktifAda) { status = "AKTIF"; aktifAda = true; } else status = "DIGANTIKAN";
    return { ...v, status };
  });
}

export const LABEL_STATUS_RIWAYAT = {
  TERJADWAL: { tone: "neutral", l: "Terjadwal" },
  AKTIF: { tone: "info", l: "Berlaku sekarang" },
  DIGANTIKAN: { tone: "success", l: "Sudah digantikan" },
};
